/**
 * Quiz page — a separate, immediate-feedback self-check alongside the dialog.
 *
 * Flow: start → show question → submit answer → show correct/incorrect +
 * explanation right there → learner clicks Next → next question or results.
 * Unlike the dialog, nothing is deferred to an end-of-session summary.
 *
 * A finished quiz is saved: its report can be reopened from My Progress, and
 * its score shows beside mastery there and on the roadmap, never inside it.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Figure } from "../../components/Figure";
import { MathText } from "../../components/Math";
import { McqChoices } from "../../components/McqChoices";
import { padHasInk, padToPng } from "../../components/HandwritingPad";
import { MathsAnswerInput, defaultAnswerMode, type MathsAnswerMode } from "../../components/MathsAnswerInput";
import { QuizTopicSummary, type AiReviewState } from "../../components/QuizTopicSummary";
import { QuizReviewList } from "../../components/QuizReviewList";
import type {
  QuizStartResponse,
  QuizAnswerResponse,
  QuizQuestion,
  QuizReviewItem,
  QuizReviewResponse,
  QuizTopicResult,
  QuizAiReview,
} from "../../types";

type Phase = "loading" | "question" | "feedback" | "complete";

export function QuizPage() {
  const { cid, lid } = useParams<{ cid: string; lid: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>("loading");
  const [sessionId, setSessionId] = useState("");
  const [lessonTitle, setLessonTitle] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentNumber, setCurrentNumber] = useState(1);
  const [question, setQuestion] = useState<QuizQuestion | null>(null);
  const [answer, setAnswer] = useState("");
  const [ink, setInk] = useState("");
  const [working, setWorking] = useState("");
  const [mathsMode, setMathsMode] = useState<MathsAnswerMode>("handwrite");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // The verdict on the answer just submitted — shown before the learner
  // moves on, not deferred to a session-end summary.
  const [feedback, setFeedback] = useState<{
    isCorrect: boolean;
    explanation: string;
    correctAnswer?: string | null;
  } | null>(null);
  const [pendingNext, setPendingNext] = useState<QuizQuestion | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finalCount, setFinalCount] = useState<{ correct: number; total: number } | null>(null);
  const [reviewItems, setReviewItems] = useState<QuizReviewItem[]>([]);
  const [topics, setTopics] = useState<QuizTopicResult[]>([]);
  const [aiReview, setAiReview] = useState<QuizAiReview | null>(null);
  const [aiState, setAiState] = useState<AiReviewState>("loading");

  const startedFor = useRef<string | null>(null);

  // The AI's reading of the attempt is one slow call, so it is fetched as
  // soon as the last answer is marked — usually ready by the time the
  // learner reaches the results.
  const loadAiReview = useCallback((id: string) => {
    setAiState("loading");
    apiGet<QuizReviewResponse>(`/api/quiz/${id}/review`)
      .then((data) => {
        if (data.topics.length) setTopics(data.topics);
        setAiReview(data.ai_review);
        setAiState(data.ai_review ? "ready" : data.ai_marking ? "failed" : "off");
      })
      .catch(() => setAiState("failed"));
  }, []);

  const clearAnswer = () => {
    setAnswer("");
    setInk("");
    setWorking("");
  };

  const startQuiz = useCallback((force = false) => {
    if (!cid || !lid || !user) return;
    const key = `${cid}/${lid}`;
    if (startedFor.current === key && !force) return;
    startedFor.current = key;
    setPhase("loading");
    setSessionId("");
    setLessonTitle("");
    setTotalQuestions(0);
    setCurrentNumber(1);
    setQuestion(null);
    clearAnswer();
    setFeedback(null);
    setPendingNext(null);
    setCorrectCount(0);
    setFinalCount(null);
    setReviewItems([]);
    setTopics([]);
    setAiReview(null);
    setAiState("loading");
    setError("");
    apiPost<QuizStartResponse>("/api/quiz/start", {
      learner_id: user.id,
      curriculum_id: cid,
      lesson_id: lid,
    })
      .then((data) => {
        setSessionId(data.session_id);
        setLessonTitle(data.lesson_title);
        setTotalQuestions(data.total_questions);
        setQuestion(data.current_question);
        setMathsMode(defaultAnswerMode(data.current_question?.answer_mode));
        if (!data.current_question || data.total_questions === 0) {
          setFinalCount({ correct: 0, total: data.total_questions });
          setPhase("complete");
        } else {
          setPhase("question");
        }
      })
      .catch((err) => {
        setError(err.message);
        startedFor.current = null;
      });
  }, [cid, lid, user?.id]);

  useEffect(() => {
    startQuiz();
  }, [startQuiz]);

  const submitAnswer = useCallback(async () => {
    const written = !question?.choices?.length;
    const handwritten = written && padHasInk(ink);
    if (!answer.trim() && !handwritten) {
      setError(written ? "Handwrite or type your answer before submitting." : "Choose an answer before submitting.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await apiPost<QuizAnswerResponse>(
        `/api/quiz/${sessionId}/answer`,
        {
          response_text: answer,
          working: working.trim() || undefined,
          answer_ink: handwritten ? ink : undefined,
          answer_image: handwritten ? padToPng(ink, 1200, { maxWidth: 1400 }) || undefined : undefined,
        }
      );
      if (result.is_correct) setCorrectCount((n) => n + 1);
      setFeedback({
        isCorrect: result.is_correct,
        explanation: result.explanation,
        correctAnswer: result.correct_answer,
      });
      setPendingNext(result.next_question ?? null);
      if (result.is_complete) {
        setFinalCount({
          correct: result.correct_count ?? 0,
          total: result.total_questions ?? totalQuestions,
        });
        setReviewItems(result.review ?? []);
        setTopics(result.topics ?? []);
        loadAiReview(sessionId);
        // Saved on the server with the last answer: refresh the pages that
        // show quiz scores and history.
        queryClient.invalidateQueries({ queryKey: ["my-progress"] });
        queryClient.invalidateQueries({ queryKey: ["roadmap"] });
      }
      setPhase("feedback");
    } catch (err: any) {
      setError(err.message || "Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  }, [answer, ink, working, question, sessionId, totalQuestions, loadAiReview, queryClient]);

  const goToNext = useCallback(() => {
    setFeedback(null);
    clearAnswer();
    if (pendingNext) {
      setCurrentNumber((n) => n + 1);
      setQuestion(pendingNext);
      setMathsMode(defaultAnswerMode(pendingNext.answer_mode));
      setPendingNext(null);
      setPhase("question");
    } else {
      setPhase("complete");
    }
  }, [pendingNext]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === "Enter" && phase === "question") submitAnswer();
  };

  const progressPct = totalQuestions > 0 ? (currentNumber / totalQuestions) * 100 : 0;

  if (phase === "loading") {
    return (
      <div className="dialog-page">
        {error ? (
          <div className="card error-card">
            <h2>Error</h2>
            <p role="alert">{error}</p>
          </div>
        ) : (
          <p className="text-muted" role="status">Starting quiz...</p>
        )}
      </div>
    );
  }

  return (
    <div className="dialog-page">
      <Link to={`/learn/${cid}/lesson/${lid}`} className="breadcrumb-link">← Back to Lesson</Link>

      <div className="dialog-progress">
        <div className="dialog-progress-label">
          Question {Math.min(currentNumber, totalQuestions)} of {totalQuestions}
          {lessonTitle && <span className="lesson-title"> — {lessonTitle} · Quiz</span>}
          <span className="lesson-title"> · {correctCount} correct so far</span>
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${phase === "complete" ? 100 : progressPct}%` }}
          />
        </div>
      </div>

      {(phase === "question" || phase === "feedback") && question && (
        <div className="question-card">
          <div className="question-type-badge">{question.type} · quiz</div>

          <div className="question-text">
            <MathText content={question.text} />
          </div>

          {question.figure && (
            <figure className="question-figure" aria-label={question.figure.title || "Question visual"}>
              {question.figure.title && (
                <figcaption className="figure-title">{question.figure.title}</figcaption>
              )}
              <Figure spec={question.figure} height={300} maxWidth={480} />
              {question.figure.caption && (
                <figcaption className="figure-caption">{question.figure.caption}</figcaption>
              )}
            </figure>
          )}

          {question.choices && question.choices.length > 0 ? (
            <McqChoices
              choices={question.choices}
              selected={answer}
              onSelect={setAnswer}
              disabled={phase === "feedback"}
            />
          ) : (
            <>
              {question.answer_hint && (
                <p className="quiz-answer-hint">{question.answer_hint}</p>
              )}
              <MathsAnswerInput
                maths={question.answer_mode === "handwriting"}
                answer={answer}
                onAnswerChange={setAnswer}
                ink={ink}
                onInkChange={setInk}
                mode={mathsMode}
                onModeChange={setMathsMode}
                working={working}
                onWorkingChange={setWorking}
                disabled={phase === "feedback" || submitting}
                onKeyDown={handleKeyDown}
              />
            </>
          )}

          {/* The verdict on THIS question, shown before moving on — the
              whole point of the quiz vs. the dialog's end-of-session summary. */}
          {phase === "feedback" && feedback && (
            <div
              className={`follow-up-hint ${feedback.isCorrect ? "quiz-feedback-correct" : "quiz-feedback-incorrect"}`}
              role="status"
            >
              <strong>{feedback.isCorrect ? "✓ Correct" : "✗ Not quite"}</strong>
              {!feedback.isCorrect && feedback.correctAnswer && (
                <p className="quiz-correct-answer">
                  <strong>Correct answer:</strong> {feedback.correctAnswer}
                </p>
              )}
              <div><MathText content={feedback.explanation} /></div>
            </div>
          )}

          {error && <div className="error-message" role="alert">{error}</div>}

          <div className="answer-footer">
            <div />
            {phase === "question" ? (
              <button className="btn btn-primary" onClick={submitAnswer} disabled={submitting}>
                {submitting ? "Checking..." : "Check answer →"}
              </button>
            ) : (
              <button className="btn btn-primary" onClick={goToNext}>
                {pendingNext ? "Next question →" : "See results →"}
              </button>
            )}
          </div>
        </div>
      )}

      {phase === "complete" && (
        <div className="card text-center">
          <h2>{totalQuestions > 0 ? "Quiz complete" : "No quiz questions available"}</h2>
          {totalQuestions > 0 ? (
            <>
              <p>
                You got {finalCount?.correct ?? correctCount} of {finalCount?.total ?? totalQuestions} correct.
              </p>
              <p className="text-muted">
                Saved to <Link to="/progress">My Progress</Link>, where you can reopen this report.
              </p>
            </>
          ) : (
            <p className="text-muted">This lesson does not have quiz questions yet.</p>
          )}
          <QuizTopicSummary topics={topics} aiReview={aiReview} aiState={aiState} />
          <QuizReviewList items={reviewItems} />
          <div className="answer-footer" style={{ justifyContent: "center", gap: 12 }}>
            <Link to={`/learn/${cid}/lesson/${lid}`} className="btn btn-secondary">
              ← Back to Lesson
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => {
                startedFor.current = null;
                startQuiz(true);
              }}
            >
              Retake quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
